import "../../components/tag/react-tags.scss";

import { List } from "immutable";
import PropTypes from "prop-types";
import React, { Fragment } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";
import ReactTags from "react-tag-autocomplete";

import FormCheckbox from "../../components/form/FormCheckbox";
import FormDropdown from "../../components/form/FormDropdown";
import FormTextField from "../../components/form/FormTextField";
import InspectorField from "../../components/inspector/InspectorField";
import InspectorGroup from "../../components/inspector/InspectorGroup";
import InspectorRow from "../../components/inspector/InspectorRow";
import checkboxStates from "../../constants/checkboxStates";
import { PropertyFormatIdentifier } from "../../constants/propertyFormats";
import { itemOperations } from "../../modules/items";
import { tagOperations, tagSelectors } from "../../modules/tags";
import { PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import preprocessorQuery from "../../utils/preprocessorQuery";
import selectors from "./selectors";

// TODO: Add FormDropdown to support list property types...

const renderDropdown = (allowed, value, onChange, isDisabled, label, itemLabel) => {
  const isIos = preprocessorQuery.isIos();
  // Get the list of available colors for the inspector dropdown
  let options = allowed
    ? allowed.map(option => {
      return {
        label: option.label,
        value: option.id
      };
    })
    : List();
  options = options.unshift({ label: "None", value: null }).toArray();

  return (
    <FormDropdown
      value={value}
      options={options}
      onChange={onChange}
      isDisabled={isDisabled}
      title={label}
      backText={getLabelWithUntitled(itemLabel)}
      arrowIcon={isIos ? "rightArrow" : null}
    />
  );
};

const renderProperty = (property, itemData, onChange) => {
  let formElement = null;
  let sameLineLabel = true;
  let rowLabel = property.label;
  const isDisabled = property.autoCalculate && itemData.hasChildren;
  switch (property.format) {
    case PropertyFormatIdentifier.text: {
      formElement = (
        <FormTextField
          multiline={false}
          value={itemData.propertyValues.get(property.id)}
          placeholder="None"
          onChange={onChange}
          disabled={isDisabled}
        />
      );
      break;
    }
    case PropertyFormatIdentifier.multitext: {
      formElement = (
        <FormTextField
          multiline={true}
          autoExpandHeight={true}
          allowNewLine={true}
          value={itemData.propertyValues.get(property.id)}
          placeholder="None"
          onChange={onChange}
          disabled={isDisabled}
        />
      );
      sameLineLabel = false;
      break;
    }
    case PropertyFormatIdentifier.list: {
      formElement = renderDropdown(
        property.allowed,
        itemData.propertyValues.get(property.id),
        onChange,
        isDisabled,
        property.label,
        itemData.label
      );
      break;
    }
    case PropertyFormatIdentifier.integer: {
      formElement = (
        <FormTextField
          numeric={true}
          value={itemData.propertyValues.get(property.id)}
          placeholder="None"
          onChange={onChange}
          disabled={isDisabled}
          isInteger={true}
        />
      );
      break;
    }
    case PropertyFormatIdentifier.double: {
      formElement = (
        <FormTextField
          numeric={true}
          value={itemData.propertyValues.get(property.id)}
          placeholder="None"
          onChange={onChange}
          disabled={isDisabled}
        />
      );
      break;
    }
    case PropertyFormatIdentifier.currency: {
      formElement = (
        <FormTextField
          numeric={true}
          value={itemData.propertyValues.get(property.id)}
          placeholder="None"
          onChange={onChange}
          disabled={isDisabled}
        />
      );
      break;
    }
    case PropertyFormatIdentifier.percentage: {
      formElement = (
        <FormTextField
          numeric={true}
          value={itemData.propertyValues.get(property.id)}
          placeholder="None"
          onChange={onChange}
          disabled={isDisabled}
        />
      );
      break;
    }
    case PropertyFormatIdentifier.boolean: {
      const propValue = itemData.propertyValues.get(property.id);
      const cbState = propValue === null ? checkboxStates.MIXED : Boolean(propValue);
      let elementLabel = null;
      //#!if iOS === true
// elementLabel = property.label;
// rowLabel = null;
      //#!endif
      formElement = (
        <FormCheckbox checkboxState={cbState} label={elementLabel} onChange={onChange} disabled={isDisabled} />
      );
      break;
    }
    default:
      break;
  }

  const key = `${property.id}-label`;

  return sameLineLabel ? (
    <InspectorRow key={key} label={rowLabel}>
      <InspectorField>{formElement}</InspectorField>
    </InspectorRow>
  ) : (
    <Fragment key={key}>
      <InspectorRow>
        <label className="inspectorLabelRow">{rowLabel}</label>
      </InspectorRow>
      <InspectorRow key={property.id}>
        <InspectorField>{formElement}</InspectorField>
      </InspectorRow>
    </Fragment>
  );
};

const InspectorPropertyTab = props => {
  const isIos = preprocessorQuery.isIos();
  const { itemIds, typeData, itemData, onPropertyChange, onSummaryChange, globalTags, onAddTags, onRemoveTags } = props;

  const itemIdsArray = itemIds.toArray();

  const propertyComponents = typeData.properties
    .map(p => renderProperty(p, itemData, value => onPropertyChange(itemIdsArray, p.id, value)))
    .toArray();

  const globalTagsArray = selectors.getTagObjects(globalTags);
  const tags = selectors.getTagObjectsForTagIds(globalTags, itemData.tags.toArray());

  const tagRowLabel = isIos ? "Tags" : null;
  const tagComponent = (
    <InspectorRow key="tagsRow" label={tagRowLabel}>
      <InspectorField>
        <ReactTags
          tags={tags}
          suggestions={globalTagsArray}
          handleAddition={newTag => {
            onAddTags(itemIdsArray, [newTag.name]);
          }}
          handleDelete={tagIndex => {
            onRemoveTags(itemIdsArray, [tags[tagIndex].name]);
          }}
          addOnBlur={true}
          autofocus={false}
          allowNew={true}
        />
      </InspectorField>
    </InspectorRow>
  );

  const summaryRowLabel = isIos ? "Summary" : null;
  const summaryComponent = (
    <InspectorRow key="summaryRow" label={summaryRowLabel}>
      <InspectorField>
        <FormTextField
          multiline={true}
          autoExpandHeight={true}
          allowNewLine={true}
          placeholder="None"
          value={itemData.summary}
          onChange={value => onSummaryChange(itemIdsArray, value)}
        />
      </InspectorField>
    </InspectorRow>
  );

  let finalComponent = null;
  //#!if iOS === true
// finalComponent = (
// <InspectorGroup label={!isIos ? "Properties" : null}>
// {summaryComponent}
// {tagComponent}
// {propertyComponents}
// </InspectorGroup>
// );
  //#!else
  finalComponent = (
    <Fragment>
      <InspectorGroup label="Summary">{summaryComponent}</InspectorGroup>
      <InspectorGroup label="Tags">{tagComponent}</InspectorGroup>
      <InspectorGroup label="Properties">{propertyComponents}</InspectorGroup>
    </Fragment>
  );
  //#!endif

  return finalComponent;
};

InspectorPropertyTab.propTypes = {
  onSummaryChange: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  itemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  itemData: PropTypeShapes.inspectorItem,
  typeData: PropTypeShapes.denormalisedTypeDefinition,
  globalTags: ImmutablePropTypes.map.isRequired,
  onAddTags: PropTypes.func.isRequired,
  onRemoveTags: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const globalTags = tagSelectors.getTags(state.present);

  return {
    globalTags
  };
};

const readonlyActions = {};

const editingActions = {
  onSummaryChange: itemOperations.editSummary,
  onPropertyChange: itemOperations.setPropertyValue,
  onAddTags: tagOperations.addTags,
  onRemoveTags: tagOperations.removeTags
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(InspectorPropertyTab);
